import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./BillingInvoicesScreen.module.scss";
import classNames from "classnames";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import ResourceList from "../../../components/resourceList/ResourceList";
import Button from "../../../components/button/Button";
import Input from "../../../components/input/Input";
import Select from "react-select";
import moment from "moment";
import { isMobile } from "react-device-detect";
import _ from "lodash";
import {
  fetchBillingInvoices,
  sendBillingInvoice,
  cancelBillingInvoice,
  togglePaidBillingInvoice,
  updateBillingInvoiceReference,
  updateBillingInvoiceReferenceResetState,
  togglePaidBillingInvoiceResetState,
  sendBillingInvoiceResetState,
  cancelBillingInvoiceResetState,
} from "../../../store/actions/admin/billingInvoices";
import AreYouSureModal from "../../../components/modals/areYouSureModal/AreYouSureModal";
import billingInvoicesService from "../../../apis/services/admin/billingInvoicesService";

const HEADER_VALUES = [
  { label: "BROJ FAKTURE", key: "invoiceNumber", sortable: false },
  { label: "KOMPANIJA", key: "companyName", sortable: false },
  { label: "PERIOD", sortable: false },
  { label: "IZDAT", key: "issueDate", sortable: false },
  { label: "ROK", key: "dueDate", sortable: false },
  { label: "STATUS", key: "status", sortable: false },
  { label: "PLAĆENO", key: "isPaid", sortable: false },
  { label: "REFERENCA", key: "reference", sortable: false },
  { label: "IZNOS", key: "total", sortable: false },
  { label: "AKCIJE", sortable: false },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Poslato" },
  { value: "paid", label: "Plaćeno" },
  { value: "overdue", label: "Zakasnelo" },
  { value: "cancelled", label: "Otkazano" },
];

const BillingInvoicesScreen = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isPaidFilter, setIsPaidFilter] = useState(null); // null = all, true = paid, false = unpaid
  const [statusFilter, setStatusFilter] = useState([]); // Array of selected statuses
  const [searchTerm, setSearchTerm] = useState("");
  const [billingPeriodStart, setBillingPeriodStart] = useState("");
  const [billingPeriodEnd, setBillingPeriodEnd] = useState("");
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingReference, setEditingReference] = useState(null);
  const [referenceValue, setReferenceValue] = useState("");
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState(null);
  const searchRef = useRef("");

  const invoices = useSelector((state) => state.billingInvoices.invoices);
  const sendInvoiceInfo = useSelector((state) => state.billingInvoices.sendInvoice);
  const cancelInvoiceInfo = useSelector((state) => state.billingInvoices.cancelInvoice);
  const togglePaid = useSelector((state) => state.billingInvoices.togglePaid);
  const updateReference = useSelector((state) => state.billingInvoices.updateReference);

  const loadData = useCallback(() => {
    const periodStart = billingPeriodStart ? moment(billingPeriodStart).startOf("day").toISOString() : null;
    const periodEnd = billingPeriodEnd ? moment(billingPeriodEnd).endOf("day").toISOString() : null;
    const statusParam = statusFilter.length > 0 ? statusFilter.map(s => s.value).join(",") : null;
    dispatch(fetchBillingInvoices(
      invoices.data.page,
      invoices.data.perPage,
      null,
      statusParam,
      searchRef.current,
      periodStart,
      periodEnd,
      isPaidFilter
    ));
  }, [dispatch, invoices.data.page, invoices.data.perPage, isPaidFilter, statusFilter, billingPeriodStart, billingPeriodEnd]);

  const loadStats = useCallback(async () => {
    try {
      const periodStart = billingPeriodStart ? moment(billingPeriodStart).startOf("day").toISOString() : null;
      const periodEnd = billingPeriodEnd ? moment(billingPeriodEnd).endOf("day").toISOString() : null;
      const statusParam = statusFilter.length > 0 ? statusFilter.map(s => s.value).join(",") : null;
      const result = await billingInvoicesService.getBillingInvoicesStats({
        companyId: null,
        status: statusParam,
        isPaid: isPaidFilter,
        billingPeriodStart: periodStart,
        billingPeriodEnd: periodEnd,
      });
      setStats(result?.data || null);
    } catch (e) {
      // stats are non-critical — silently fail to keep the table visible
      setStats(null);
    }
  }, [isPaidFilter, statusFilter, billingPeriodStart, billingPeriodEnd]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh stats whenever filters change OR after any mutation that re-runs loadData
  useEffect(() => {
    loadStats();
  }, [loadStats, invoices.data.total]);

  useEffect(() => {
    if (sendInvoiceInfo.success) {
      toast.success("Faktura uspešno poslata");
      setSendModalOpen(false);
      setSelectedInvoice(null);
      dispatch(sendBillingInvoiceResetState());
      loadData();
    }
  }, [sendInvoiceInfo.success, dispatch, loadData]);

  useEffect(() => {
    if (sendInvoiceInfo.error) {
      toast.error("Greška pri slanju fakture");
      dispatch(sendBillingInvoiceResetState());
    }
  }, [sendInvoiceInfo.error, dispatch]);

  useEffect(() => {
    if (cancelInvoiceInfo.success) {
      toast.success("Faktura uspešno otkazana");
      setCancelModalOpen(false);
      setSelectedInvoice(null);
      dispatch(cancelBillingInvoiceResetState());
      loadData();
    }
  }, [cancelInvoiceInfo.success, dispatch, loadData]);

  useEffect(() => {
    if (cancelInvoiceInfo.error) {
      toast.error("Greška pri otkazivanju fakture");
      dispatch(cancelBillingInvoiceResetState());
    }
  }, [cancelInvoiceInfo.error, dispatch]);

  useEffect(() => {
    if (togglePaid.success) {
      toast.success("Status plaćanja uspešno promenjen");
      dispatch(togglePaidBillingInvoiceResetState());
      loadData();
    }
  }, [togglePaid.success, dispatch, loadData]);

  useEffect(() => {
    if (togglePaid.error) {
      toast.error("Greška pri promeni statusa plaćanja");
      dispatch(togglePaidBillingInvoiceResetState());
    }
  }, [togglePaid.error, dispatch]);

  useEffect(() => {
    if (updateReference.success) {
      toast.success("Referenca uspešno ažurirana");
      dispatch(updateBillingInvoiceReferenceResetState());
      loadData();
    }
  }, [updateReference.success, dispatch, loadData]);

  useEffect(() => {
    if (updateReference.error) {
      toast.error("Greška pri ažuriranju reference");
      dispatch(updateBillingInvoiceReferenceResetState());
    }
  }, [updateReference.error, dispatch]);

  const onLoadPage = (page) => {
    const periodStart = billingPeriodStart ? moment(billingPeriodStart).startOf("day").toISOString() : null;
    const periodEnd = billingPeriodEnd ? moment(billingPeriodEnd).endOf("day").toISOString() : null;
    const statusParam = statusFilter.length > 0 ? statusFilter.map(s => s.value).join(",") : null;
    dispatch(fetchBillingInvoices(page, invoices.data.perPage, null, statusParam, searchRef.current, periodStart, periodEnd, isPaidFilter));
  };

  const handleStatusFilterChange = (selectedStatuses) => {
    setStatusFilter(selectedStatuses || []);
    const periodStart = billingPeriodStart ? moment(billingPeriodStart).startOf("day").toISOString() : null;
    const periodEnd = billingPeriodEnd ? moment(billingPeriodEnd).endOf("day").toISOString() : null;
    const statusParam = selectedStatuses && selectedStatuses.length > 0 ? selectedStatuses.map(s => s.value).join(",") : null;
    dispatch(fetchBillingInvoices(1, invoices.data.perPage, null, statusParam, searchRef.current, periodStart, periodEnd, isPaidFilter));
  };

  const handleIsPaidFilterToggle = () => {
    // Cycle through: null (all) -> true (paid) -> false (unpaid) -> null (all)
    let newFilter;
    if (isPaidFilter === null) {
      newFilter = true; // Show only paid
    } else if (isPaidFilter === true) {
      newFilter = false; // Show only unpaid
    } else {
      newFilter = null; // Show all
    }
    setIsPaidFilter(newFilter);
    const periodStart = billingPeriodStart ? moment(billingPeriodStart).startOf("day").toISOString() : null;
    const periodEnd = billingPeriodEnd ? moment(billingPeriodEnd).endOf("day").toISOString() : null;
    const statusParam = statusFilter.length > 0 ? statusFilter.map(s => s.value).join(",") : null;
    dispatch(fetchBillingInvoices(1, invoices.data.perPage, null, statusParam, searchRef.current, periodStart, periodEnd, newFilter));
  };

  const handleSearch = useCallback(
    _.debounce((searchValue) => {
      searchRef.current = searchValue;
      const periodStart = billingPeriodStart ? moment(billingPeriodStart).startOf("day").toISOString() : null;
      const periodEnd = billingPeriodEnd ? moment(billingPeriodEnd).endOf("day").toISOString() : null;
      const statusParam = statusFilter.length > 0 ? statusFilter.map(s => s.value).join(",") : null;
      dispatch(fetchBillingInvoices(1, invoices.data.perPage, null, statusParam, searchValue, periodStart, periodEnd, isPaidFilter));
    }, 500),
    [dispatch, invoices.data.perPage, isPaidFilter, statusFilter, billingPeriodStart, billingPeriodEnd]
  );

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    handleSearch(value);
  };

  const handleBillingPeriodStartChange = (e) => {
    const value = e.target.value;
    if (value && billingPeriodEnd && moment(value).isAfter(moment(billingPeriodEnd))) {
      toast.warning("Datum početka ne može biti posle datuma završetka");
      return;
    }
    setBillingPeriodStart(value);
    const periodStart = value ? moment(value).startOf("day").toISOString() : null;
    const periodEnd = billingPeriodEnd ? moment(billingPeriodEnd).endOf("day").toISOString() : null;
    const statusParam = statusFilter.length > 0 ? statusFilter.map(s => s.value).join(",") : null;
    dispatch(fetchBillingInvoices(1, invoices.data.perPage, null, statusParam, searchRef.current, periodStart, periodEnd, isPaidFilter));
  };

  const handleBillingPeriodEndChange = (e) => {
    const value = e.target.value;
    if (value && billingPeriodStart && moment(value).isBefore(moment(billingPeriodStart))) {
      toast.warning("Datum završetka ne može biti pre datuma početka");
      return;
    }
    setBillingPeriodEnd(value);
    const periodStart = billingPeriodStart ? moment(billingPeriodStart).startOf("day").toISOString() : null;
    const periodEnd = value ? moment(value).endOf("day").toISOString() : null;
    const statusParam = statusFilter.length > 0 ? statusFilter.map(s => s.value).join(",") : null;
    dispatch(fetchBillingInvoices(1, invoices.data.perPage, null, statusParam, searchRef.current, periodStart, periodEnd, isPaidFilter));
  };

  const handleSendClick = (invoice) => {
    setSelectedInvoice(invoice);
    setSendModalOpen(true);
  };

  const handleCancelClick = (invoice) => {
    setSelectedInvoice(invoice);
    setCancelModalOpen(true);
  };

  const handleSendConfirm = () => {
    if (selectedInvoice) {
      dispatch(sendBillingInvoice(selectedInvoice.id));
    }
  };

  const handleCancelConfirm = () => {
    if (selectedInvoice) {
      dispatch(cancelBillingInvoice(selectedInvoice.id));
    }
  };

  const handleTogglePaid = (invoice) => {
    dispatch(togglePaidBillingInvoice(invoice.id));
  };

  const handleReferenceEdit = (invoice) => {
    setEditingReference(invoice.id);
    setReferenceValue(invoice.reference || "");
  };

  const handleReferenceSave = (invoiceId) => {
    dispatch(updateBillingInvoiceReference(invoiceId, referenceValue));
    setEditingReference(null);
    setReferenceValue("");
  };

  const handleReferenceCancel = () => {
    setEditingReference(null);
    setReferenceValue("");
  };

  const handleReferenceKeyPress = (e, invoiceId) => {
    if (e.key === "Enter") {
      handleReferenceSave(invoiceId);
    } else if (e.key === "Escape") {
      handleReferenceCancel();
    }
  };

  const handleExport = async (format = 'xlsx') => {
    setExporting(true);
    try {
      const periodStart = billingPeriodStart ? moment(billingPeriodStart).startOf("day").toISOString() : null;
      const periodEnd = billingPeriodEnd ? moment(billingPeriodEnd).endOf("day").toISOString() : null;
      const statusParam = statusFilter.length > 0 ? statusFilter.map(s => s.value) : null;

      await billingInvoicesService.exportBillingInvoices({
        billingPeriodStart: periodStart,
        billingPeriodEnd: periodEnd,
        companyId: null, // Could be added if needed
        status: statusParam,
        isPaid: isPaidFilter,
        format: format,
      });

      toast.success(`Fakture uspešno izvezene u ${format.toUpperCase()} formatu`);
    } catch (error) {
      console.error("Export error:", error);
      if (error.response?.status === 404) {
        toast.error("Nema faktura za izvoz za odabrane kriterijume");
      } else {
        toast.error("Greška pri izvozu faktura");
      }
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "draft":
        return styles.draft;
      case "sent":
        return styles.sent;
      case "paid":
        return styles.paid;
      case "overdue":
        return styles.overdue;
      case "cancelled":
        return styles.cancelled;
      default:
        return "";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "draft":
        return "Draft";
      case "sent":
        return "Poslato";
      case "paid":
        return "Plaćeno";
      case "overdue":
        return "Zakasnelo";
      case "cancelled":
        return "Otkazano";
      default:
        return status;
    }
  };

  const renderInvoiceRow = (invoice, index) => {
    const issueDate = invoice.issueDate
      ? moment(invoice.issueDate).format("DD-MM-YYYY")
      : "-";
    const dueDate = invoice.dueDate
      ? moment(invoice.dueDate).format("DD-MM-YYYY")
      : "-";
    const periodStart = invoice.billingPeriodStart
      ? moment(invoice.billingPeriodStart).format("DD-MM-YYYY")
      : "-";
    const periodEnd = invoice.billingPeriodEnd
      ? moment(invoice.billingPeriodEnd).format("DD-MM-YYYY")
      : "-";
    const period = `${periodStart} - ${periodEnd}`;

    if (isMobile) {
      return (
        <div className={styles.listCard} key={invoice.id}>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Broj fakture</p>
            <p className={styles.rowCardValue}>{invoice.invoiceNumber}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Kompanija</p>
            <p className={styles.rowCardValue}>{invoice.company?.name || "-"}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Period</p>
            <p className={styles.rowCardValue}>{period}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Status</p>
            <span className={classNames(styles.statusBadge, getStatusBadgeClass(invoice.status))}>
              {getStatusLabel(invoice.status)}
            </span>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Plaćeno</p>
            <div className={styles.toggleWrapper}>
              <span className={classNames(styles.statusBadge, invoice.isPaid ? styles.paid : styles.unpaid)}>
                {invoice.isPaid ? "Da" : "Ne"}
              </span>
              <button
                className={styles.toggleButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleTogglePaid(invoice);
                }}
                disabled={togglePaid.attempt}
                title={invoice.isPaid ? "Označi kao neplaćeno" : "Označi kao plaćeno"}
              >
                {invoice.isPaid ? "✕" : "✓"}
              </button>
            </div>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Referenca</p>
            {editingReference === invoice.id ? (
              <div className={styles.referenceEditWrapper}>
                <input
                  type="text"
                  className={styles.referenceInput}
                  value={referenceValue}
                  onChange={(e) => setReferenceValue(e.target.value)}
                  onKeyDown={(e) => handleReferenceKeyPress(e, invoice.id)}
                  onBlur={() => handleReferenceSave(invoice.id)}
                  autoFocus
                  maxLength={255}
                />
              </div>
            ) : (
              <div className={styles.referenceDisplay} onClick={() => handleReferenceEdit(invoice)}>
                <p className={styles.rowCardValue}>{invoice.reference || "-"}</p>
                <span className={styles.editIcon}>✏️</span>
              </div>
            )}
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Iznos</p>
            <p className={styles.rowCardValue}>{typeof invoice.total === "number" ? invoice.total.toFixed(2) : (parseFloat(invoice.total) || 0).toFixed(2)} RSD</p>
          </div>
          <div className={styles.rowCard}>
            <button
              className={styles.viewButton}
              onClick={() => history.push(`/billing-invoices/${invoice.id}`)}
            >
              Pregled
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={classNames(
          styles.row,
          index % 2 === 0 ? styles.even : styles.odd
        )}
        key={invoice.id}
      >
        <div className={styles.cell}>
          <p className={styles.invoiceNumber}>{invoice.invoiceNumber}</p>
        </div>
        <div className={styles.cell}>
          <p>{invoice.company?.name || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p className={styles.period}>{period}</p>
        </div>
        <div className={styles.cell}>
          <p>{issueDate}</p>
        </div>
        <div className={styles.cell}>
          <p>{dueDate}</p>
        </div>
        <div className={styles.cell}>
          <span className={classNames(styles.statusBadge, getStatusBadgeClass(invoice.status))}>
            {getStatusLabel(invoice.status)}
          </span>
        </div>
        <div className={styles.cell}>
          <div className={styles.toggleWrapper}>
            <span className={classNames(styles.statusBadge, invoice.isPaid ? styles.paid : styles.unpaid)}>
              {invoice.isPaid ? "Da" : "Ne"}
            </span>
            <button
              className={styles.toggleButton}
              onClick={(e) => {
                e.stopPropagation();
                handleTogglePaid(invoice);
              }}
              disabled={togglePaid.attempt}
              title={invoice.isPaid ? "Označi kao neplaćeno" : "Označi kao plaćeno"}
            >
              {invoice.isPaid ? "✕" : "✓"}
            </button>
          </div>
        </div>
        <div className={styles.cell}>
          {editingReference === invoice.id ? (
            <div className={styles.referenceEditWrapper}>
              <input
                type="text"
                className={styles.referenceInput}
                value={referenceValue}
                onChange={(e) => setReferenceValue(e.target.value)}
                onKeyDown={(e) => handleReferenceKeyPress(e, invoice.id)}
                onBlur={() => handleReferenceSave(invoice.id)}
                autoFocus
                maxLength={255}
              />
            </div>
          ) : (
            <div className={styles.referenceDisplay} onClick={() => handleReferenceEdit(invoice)} title="Kliknite za izmenu">
              <p>{invoice.reference || "-"}</p>
              <span className={styles.editIcon}>✏️</span>
            </div>
          )}
        </div>
        <div className={styles.cell}>
          <p className={styles.amount}>{typeof invoice.total === "number" ? invoice.total.toFixed(2) : (parseFloat(invoice.total) || 0).toFixed(2)} RSD</p>
        </div>
        <div className={styles.cell}>
          <div className={styles.actions}>
            <button
              className={styles.viewButton}
              onClick={() => history.push(`/billing-invoices/${invoice.id}`)}
            >
              Pregled
            </button>
            {invoice.status === "draft" && (
              <button
                className={styles.sendButton}
                onClick={() => handleSendClick(invoice)}
              >
                Pošalji
              </button>
            )}
            {invoice.status !== "paid" && invoice.status !== "cancelled" && (
              <button
                className={styles.cancelButton}
                onClick={() => handleCancelClick(invoice)}
              >
                Otkaži
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <AreYouSureModal
        modalOpen={sendModalOpen}
        closeModal={() => {
          setSendModalOpen(false);
          setSelectedInvoice(null);
        }}
        onConfirm={handleSendConfirm}
        title="Pošalji fakturu"
        message={
          selectedInvoice && (
            <div>
              <p>Da li ste sigurni da želite da pošaljete fakturu:</p>
              <p><strong>{selectedInvoice.invoiceNumber}</strong></p>
            </div>
          )
        }
      />

      <AreYouSureModal
        modalOpen={cancelModalOpen}
        closeModal={() => {
          setCancelModalOpen(false);
          setSelectedInvoice(null);
        }}
        onConfirm={handleCancelConfirm}
        title="Otkaži fakturu"
        message={
          selectedInvoice && (
            <div>
              <p>Da li ste sigurni da želite da otkažete fakturu:</p>
              <p><strong>{selectedInvoice.invoiceNumber}</strong></p>
              <p className={styles.warningText}>
                <strong>Upozorenje:</strong> Ova akcija ne može biti poništena.
              </p>
            </div>
          )
        }
      />

      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Fakturisanje</h1>
        <div className={styles.headerActions}>
          <Button
            name={exporting ? "Izvoz..." : "Izvezi fakture"}
            onClick={() => handleExport('xlsx')}
            disabled={exporting}
          />
          <Button
            name="Masovno kreiranje"
            onClick={() => history.push("/billing-invoices/bulk-create")}
          />
          <Button
            name="Prefiksi faktura"
            onClick={() => history.push("/invoice-prefixes")}
          />
          <Button
            name="+ Nova šema"
            onClick={() => history.push("/invoice-schemas/new")}
          />
          <Button
            name="Šeme faktura"
            onClick={() => history.push("/invoice-schemas")}
          />
          <Button
            name="+ Nova faktura"
            onClick={() => history.push("/billing-invoices/new")}
          />
        </div>
        </div>

        {stats && (
          <div className={styles.stats}>
            <div className={classNames(styles.statCard, styles.statTotal)}>
              <span className={styles.statLabel}>Ukupno</span>
              <span className={styles.statValue}>
                {(stats.totalAmount || 0).toLocaleString("sr-RS", { maximumFractionDigits: 2 })} RSD
              </span>
              <span className={styles.statSub}>{stats.totalCount} faktura</span>
            </div>
            <div className={classNames(styles.statCard, styles.statPaid)}>
              <span className={styles.statLabel}>Plaćeno</span>
              <span className={styles.statValue}>
                {(stats.paidAmount || 0).toLocaleString("sr-RS", { maximumFractionDigits: 2 })} RSD
              </span>
              <span className={styles.statSub}>{stats.paidCount} faktura</span>
            </div>
            <div className={classNames(styles.statCard, styles.statDue)}>
              <span className={styles.statLabel}>Duguje</span>
              <span className={styles.statValue}>
                {(stats.unpaidAmount || 0).toLocaleString("sr-RS", { maximumFractionDigits: 2 })} RSD
              </span>
              <span className={styles.statSub}>{stats.unpaidCount} faktura</span>
            </div>
            <div className={classNames(styles.statCard, styles.statOverdue)}>
              <span className={styles.statLabel}>Zakasnelo</span>
              <span className={styles.statValue}>
                {(stats.overdueAmount || 0).toLocaleString("sr-RS", { maximumFractionDigits: 2 })} RSD
              </span>
              <span className={styles.statSub}>{stats.overdueCount} faktura</span>
            </div>
          </div>
        )}

        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Status:</label>
            <Select
              isMulti
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              placeholder="Izaberite statuse..."
              styles={{
                container: (provided) => ({
                  ...provided,
                  minWidth: "200px",
                  width: "100%",
                }),
                control: (provided) => ({
                  ...provided,
                  minHeight: "40px",
                }),
              }}
            />
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Plaćeno:</label>
            <button
              className={classNames(
                styles.paidToggle,
                isPaidFilter === true && styles.paidToggleActive,
                isPaidFilter === false && styles.unpaidToggleActive
              )}
              onClick={handleIsPaidFilterToggle}
            >
              {isPaidFilter === null && "Sve"}
              {isPaidFilter === true && "Plaćeno"}
              {isPaidFilter === false && "Nije plaćeno"}
            </button>
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Period od:</label>
            <input
              type="date"
              className={styles.dateInput}
              value={billingPeriodStart}
              onChange={handleBillingPeriodStartChange}
              max={billingPeriodEnd || undefined}
            />
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Period do:</label>
            <input
              type="date"
              className={styles.dateInput}
              value={billingPeriodEnd}
              onChange={handleBillingPeriodEndChange}
              min={billingPeriodStart || undefined}
            />
          </div>
          <div className={styles.searchGroup}>
            <Input
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Pretraži fakture..."
            />
          </div>
        </div>

        <div className={styles.tableSection}>
          <ResourceList
            resource={invoices.data}
            onSort={() => {}}
            onLoadPage={onLoadPage}
            headerValues={HEADER_VALUES}
            rowRender={renderInvoiceRow}
            showLoader={true}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BillingInvoicesScreen;

